import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import { rem } from '@vimeo/bokeh';
import { bokehTheme as theme } from '@vimeo/bokeh-theme';
import { translate } from '@vimeo/i18n-runtime';
import { PopOver, Pop } from '@vimeo/iris/components';
import { Paragraph } from '@vimeo/iris/typography';
import { ViewerContext } from '@vimeo/viewer';

import { BreakPoints } from '../../../libs/MediaQuery';
import { PopItem } from '../../nav/desktop/userMenus/PopOverList';
import { Copyright } from '../Copyright';
import DoNotSellLink from '../DoNotSellLink';
import ChevronIcon from '../icons/ChevronIcon';
import * as Links from '../Links';
import { ModalCTAs } from '../MinimalFooter';

const Footer = styled.footer`
  background: ${({ theme: t }) =>
    t.name === 'dark' ? theme.colors.gray['900'] : theme.colors.gray['50']};
  display: flex;
  justify-content: space-between;
  padding: ${rem(8)} ${rem(32)} ${rem(16)};
  align-items: center;
  @media (width < ${rem(BreakPoints.sm)}) {
    display: none;
  }
`;

const Text = styled(Paragraph).attrs({ size: '3' })`
  margin-bottom: 0;
  margin-top: 0;

  &:hover {
    cursor: pointer;
  }
`;

const Section = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalCta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  p {
    &:hover {
      cursor: pointer;
      color: ${theme.colors.gray['800']};
    }
  }
`;

const MenuCta = styled.div`
  margin-bottom: ${rem(1)};
  display: flex;
  &:hover {
    cursor: pointer;
    svg {
      transform: rotate(180deg);
    }
  }
`;

const FooterNav = styled(Section)`
  a {
    text-decoration: none;
  }
  p {
    color: ${theme.colors.gray['200']};
  }
  path,
  circle {
    fill: ${theme.colors.gray['200'] as string};
  }
  & > * {
    &:not(:first-child) {
      margin-left: ${rem(24)};
    }
    &:first-child {
      margin-bottom: 0;
    }
    &:hover {
      p {
        color: ${({ theme: t }) =>
          t.name === 'dark' ? theme.colors.gray['50'] : theme.colors.gray['800']};
      }
      path,
      circle {
        fill: ${({ theme: t }) =>
          t.name === 'dark'
            ? (theme.colors.gray['50'] as string)
            : (theme.colors.gray['800'] as string)};
      }
    }
  }
`;

interface Props {
  impressumQualifies?: boolean;
  modalCtas: ModalCTAs;
  isDoNotSellReady?: boolean;
}

function DesktopMinimalFooter({ impressumQualifies, modalCtas, isDoNotSellReady }: Props) {
  const [isMenuExpanded, setMenuExpanded] = useState<boolean>(false);
  const viewer = useContext(ViewerContext);
  const isSimplifiedSite = viewer?.isSimplifiedSite;

  const legalLinks = Links.getLegalLinks().filter(
    (link) =>
      (impressumQualifies ? true : link.title !== 'Impressum') &&
      (!isSimplifiedSite || link.forSimplifiedSite === isSimplifiedSite),
  );

  return (
    <Footer>
      <FooterNav>
        <Copyright />
        <Text>
          <Links.Upgrade />
        </Text>
        <ModalCta>
          <Text onClick={modalCtas.language.open}>{modalCtas.language.text}</Text>
        </ModalCta>
        <Text>
          <Links.Help />
        </Text>
        <ModalCta>
          <Text onClick={modalCtas.contentPrefs.open}>{modalCtas.contentPrefs.text}</Text>
        </ModalCta>
        <PopOver
          attach="top"
          content={
            <Pop.List>
              {legalLinks.map((link, i) => (
                <a
                  href={link.href}
                  key={`link_${i}`}
                  css={`
                    text-decoration: none;
                  `}
                >
                  <PopItem key={i} title={link.title}>
                    {link.label}
                  </PopItem>
                </a>
              ))}
              <DoNotSellLink footerType="minimal" isDoNotSellReady={isDoNotSellReady} />
            </Pop.List>
          }
        >
          <MenuCta onClick={() => setMenuExpanded(true)}>
            <Text>{translate('Legal')}</Text>
            <ChevronIcon style={{ transform: `rotate(${isMenuExpanded ? '180' : '0'}deg)` }} />
          </MenuCta>
        </PopOver>
      </FooterNav>
    </Footer>
  );
}

export default DesktopMinimalFooter;
